import type { HotelService } from './services';

/**
 * Offers: promotional banners the owner writes and guests see as a carousel on
 * the home page. Configured under Manage → Settings → Offers.
 *
 * **Display only.** An offer never changes a price and is never applied to a
 * request — `createServiceRequest` copies prices from the catalogue exactly as
 * it did before offers existed. The banner is the owner's word to the guest;
 * honouring it is the desk's job, the way a printed tent card would be. There
 * is no start or end date either: the owner switches an offer on and off by
 * hand, and that is the whole lifecycle.
 *
 * One document per offer under `hotels/{hotelId}/offers`, rather than a list
 * on a settings document, because each carries an optional image and is edited
 * on its own — the same reason a dish is its own document and a housekeeping
 * row is not.
 *
 * `service` says which service the offer belongs to, and it does two things:
 * the banner deep-links there, and `getGuestServices` drops the offer while
 * that service is switched off, so a hotel that turns the restaurant off does
 * not keep advertising it. `'general'` is for hotel-wide offers with no link.
 */

export const OFFER_SERVICE_GENERAL = 'general';

export type OfferService = HotelService | typeof OFFER_SERVICE_GENERAL;

/**
 * The services an offer can attach to. Wi-Fi is left out: nobody discounts it.
 * Places to Explore is left out too — tour packages carry their own discount.
 */
export const OFFER_SERVICES: readonly OfferService[] = [
  OFFER_SERVICE_GENERAL,
  'restaurant',
  'store',
  'laundry',
  'housekeeping',
];

export const OFFER_DISCOUNT_TYPE = {
  PERCENT: 'percent',
  FLAT: 'flat',
} as const;

export type OfferDiscountType = (typeof OFFER_DISCOUNT_TYPE)[keyof typeof OFFER_DISCOUNT_TYPE];

export const OFFER_DISCOUNT_TYPES: readonly OfferDiscountType[] = [
  OFFER_DISCOUNT_TYPE.PERCENT,
  OFFER_DISCOUNT_TYPE.FLAT,
];

/** One object rather than two nullable fields, so an offer is never both or neither. */
export interface OfferDiscount {
  type: OfferDiscountType;
  /** 1–100 for percent; a whole-currency amount with at most two decimals for flat. */
  value: number;
}

export const MAX_OFFER_NAME_LENGTH = 60;
export const MAX_OFFER_INFO_LENGTH = 200;
export const MAX_OFFER_BADGE_LENGTH = 16;
/** Same ceiling as a menu price: a flat discount larger than any bill is a typo. */
export const MAX_OFFER_FLAT_VALUE = 1_000_000;
/** A carousel longer than this is not read; the owner is asked to retire one first. */
export const MAX_OFFERS = 20;

export interface OfferImageRef {
  path: string;
}

/** `hotels/{hotelId}/offers/{offerId}` */
export interface OfferDoc {
  name: string;
  /** `name` lower-cased and whitespace-collapsed; what the uniqueness check queries. */
  nameKey: string;
  info: string;
  discount: OfferDiscount;
  service: OfferService;
  /** Owner-written corner label. Absent → derived from `discount` on the client. */
  badge?: string;
  /**
   * The banner background as a Storage path under `offerImageStoragePath`,
   * never a download URL — see `storagePaths.ts`. Absent → the default banner
   * for `service` (`OFFER_BANNER_THEME`).
   */
  image?: OfferImageRef;
  enabled: boolean;
  /** Carousel position, ascending. Assigned on create, rewritten by `reorderOffers`. */
  sortOrder: number;
  createdAt?: unknown;
  updatedAt?: unknown;
}

/** Case- and space-insensitive identity, so "happy hour" is "Happy Hour". */
export const offerNameKey = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * The corner label when the owner did not write one: "20% OFF" or "₹100 OFF".
 * Formatted here so the owner's preview and the guest's banner agree.
 */
export function offerBadgeLabel(offer: { badge?: string; discount: OfferDiscount }): string {
  if (offer.badge) return offer.badge;
  const { type, value } = offer.discount;
  if (type === OFFER_DISCOUNT_TYPE.PERCENT) return `${value}% OFF`;
  const hasPaise = Math.round(value * 100) % 100 !== 0;
  return `₹${hasPaise ? value.toFixed(2) : String(Math.round(value))} OFF`;
}

/**
 * Default banner look per service, for an offer without an image. Plain hex
 * values so the RN app and the web page paint the same thing from one source:
 * the web draws a gradient from `from` to `to`, the app (which has no gradient
 * primitive) fills with `from` and overlays translucent shapes in `to`.
 */
export interface OfferBannerTheme {
  from: string;
  to: string;
}

export const OFFER_BANNER_THEME: Readonly<Record<OfferService, OfferBannerTheme>> = {
  general: { from: '#312E81', to: '#6366F1' },
  restaurant: { from: '#9A3412', to: '#F97316' },
  store: { from: '#065F46', to: '#10B981' },
  laundry: { from: '#1E40AF', to: '#3B82F6' },
  housekeeping: { from: '#6B21A8', to: '#A855F7' },
  wifi: { from: '#115E59', to: '#14B8A6' },
  placesToExplore: { from: '#0E7490', to: '#22D3EE' },
};

/** Human label for the service picker and the banner's small caption. */
export const OFFER_SERVICE_LABEL: Readonly<Record<OfferService, string>> = {
  general: 'Hotel-wide',
  restaurant: 'Restaurant',
  store: 'Hotel Store',
  laundry: 'Laundry',
  housekeeping: 'Housekeeping',
  wifi: 'Wi-Fi',
  placesToExplore: 'Places to Explore',
};
